
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { DatabaseMaterial } from '@/hooks/useMaterials';
import { useCurrentOrganization } from '@/contexts/OrganizationContext';

// Colonne che NON vanno inserite duplicando un materiale:
// - identità / timestamp gestiti dal DB
// - flag di sistema / catalogo che una copia utente non deve ereditare
// - campi calcolati di sola lettura provenienti dalla view materials_with_pricing
//   (non esistono nella tabella base `materials` → l'INSERT fallirebbe con 400)
const NON_INSERTABLE_KEYS = [
  'id',
  'created_at',
  'updated_at',
  'created_by',
  'organization_id',
  'is_system',
  'starter_category',
  // campi solo-view (materials_with_pricing)
  'net_price',
  'family_discount_chain',
  'family_discount_pct',
  'extra_discount_pct',
  'total_discount_pct',
  'has_family_discount',
  'has_extra_discount',
  'discount_id',
  'discount_org_id',
  'discount_valid_until',
  // personalizzazioni per-org (anch'esse solo-view)
  'customization_id',
  'custom_net_price',
  'custom_installation_time_per_sqm',
  'has_price_override',
  'has_time_override',
  'catalog_net_price',
  'catalog_installation_time_per_sqm',
  'custom_family_id',
  'effective_family_id',
  'has_family_override',
] as const;

export const useMaterialDuplicate = () => {
  const queryClient = useQueryClient();
  const { currentOrganizationId } = useCurrentOrganization();

  return useMutation({
    mutationFn: async (material: DatabaseMaterial) => {
      if (!currentOrganizationId) {
        throw new Error('Nessuna organizzazione selezionata: impossibile duplicare il materiale.');
      }

      const { data: userData } = await supabase.auth.getUser();
      const userId = userData.user?.id;

      // Copia pulita: rimuovi le colonne non inseribili, poi timbra org + autore
      const clean = { ...(material as Record<string, unknown>) };
      for (const key of NON_INSERTABLE_KEYS) {
        delete clean[key];
      }

      const duplicatedMaterial = {
        ...clean,
        name: `${material.name} (Copia)`,
        code: `${material.code}_COPY_${Date.now()}`, // timestamp per evitare codici duplicati
        organization_id: currentOrganizationId, // la copia finisce nel catalogo della propria org
        created_by: userId,
        is_active: true,
      };

      const { data, error } = await supabase
        .from('materials')
        .insert([duplicatedMaterial as never])
        .select()
        .single();

      if (error) {
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      toast.success('Materiale duplicato con successo!');
      queryClient.invalidateQueries({ queryKey: ['materials'] });
    },
    onError: (error: unknown) => {
      // Gli errori Supabase (PostgrestError) non sono Error JS: estrai .message
      const message =
        error && typeof error === 'object' && 'message' in error
          ? String((error as { message: unknown }).message)
          : String(error);
      console.error('Error duplicating material:', error);
      toast.error(`Errore durante la duplicazione del materiale: ${message}`);
    },
  });
};
